/**
 * AgentRunPanel - the agent builder's Sandbox tab: live test runs against the unsaved editor state.
 *
 * Pick an item (or type values per variable), hit Run, switch to Prompt & Context to edit, come
 * back and hit Run again - no saving and no separate sandbox view. Stays mounted while hidden
 * (see AgentBuilder) so inputs and the last result survive tab switches. Runs go to /agents/testing/draft/stream, which
 * renders and streams exactly like the saved-agent sandbox.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AgentConfig } from '../types/agent';
import { apiClient } from '../api/client';
import { resolveItemReference } from './AgentSandbox';
import { contextFieldSources } from './VariableConfigPopover';
import { PlayIcon } from './icons';
import './AgentRunPanel.css';

interface RunResult {
  renderedSystem: string;
  renderedUser: string;
  thinking: string;
  response: string;
  status: 'streaming' | 'complete' | 'error';
  error?: string;
  validation?: { valid: boolean; errors?: unknown };
  durationMs?: number;
}

interface AgentRunPanelProps {
  agent: Partial<AgentConfig>;
  // Jump to the Prompt & Context tab, from the "edit prompt" shortcut next to the result.
  onEditPrompt: () => void;
}

export const AgentRunPanel: React.FC<AgentRunPanelProps> = ({ agent, onEditPrompt }) => {
  const [itemReference, setItemReference] = useState('');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [result, setResult] = useState<RunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // The item reference last resolved, so re-runs against the same item skip the ALM lookup.
  const resolvedItem = useRef<{ reference: string; id: string } | null>(null);

  const fields = agent.context_fields || [];
  // A variable with an item-derived source fills itself from the item; the rest need a typed value.
  const itemDriven = (name: string) => {
    const field = fields.find(f => f.name === name);
    return !!field && contextFieldSources(field).some(s => s.startsWith('item.') || s.startsWith('computed.'));
  };

  const run = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setRunning(true);
    const started = performance.now();
    setResult({ renderedSystem: '', renderedUser: '', thinking: '', response: '', status: 'streaming' });

    try {
      const reference = itemReference.trim();
      let itemId: string | undefined;
      if (reference) {
        if (resolvedItem.current?.reference !== reference) {
          resolvedItem.current = { reference, id: await resolveItemReference(reference) };
        }
        itemId = resolvedItem.current.id;
      }
      const context = Object.fromEntries(Object.entries(overrides).filter(([, v]) => v.trim() !== ''));
      const response = await apiClient.streamDraftAgentTest(agent, context, itemId, controller.signal);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const handle = (line: string) => {
        if (!line.trim()) return;
        const data = JSON.parse(line);
        setResult(prev => {
          if (!prev) return prev;
          switch (data.type) {
            case 'metadata':
              return { ...prev, renderedSystem: data.rendered_prompt?.system ?? '', renderedUser: data.rendered_prompt?.user ?? '' };
            case 'thinking':
              return { ...prev, thinking: prev.thinking + data.content };
            case 'chunk':
              return { ...prev, response: prev.response + data.content };
            case 'complete':
              return { ...prev, status: 'complete', validation: data.validation, durationMs: performance.now() - started };
            case 'error':
              return { ...prev, status: 'error', error: data.error };
            default:
              return prev;
          }
        });
      };
      // Buffer partial lines across reads - one NDJSON line can span several reader.read() calls.
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          lines.forEach(handle);
        }
        if (buffer.trim()) handle(buffer);
      }
      setResult(prev => (prev && prev.status === 'streaming' ? { ...prev, status: 'complete', durationMs: performance.now() - started } : prev));
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setResult(prev => ({ ...(prev ?? { renderedSystem: '', renderedUser: '', thinking: '', response: '' }), status: 'error', error: err.message }));
    } finally {
      if (abortRef.current === controller) setRunning(false);
    }
  };

  // Ctrl/Cmd+Enter runs from anywhere in the builder - on another tab too, the result is waiting
  // here when you switch back.
  const runRef = useRef(run);
  runRef.current = run;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !(e.target as HTMLElement)?.closest?.('.variable-popover')) {
        e.preventDefault();
        runRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      abortRef.current?.abort();
    };
  }, []);

  const canRun = !!agent.model?.model_slug && !!agent.prompt?.user_prompt_template?.trim();
  let prettyResponse = result?.response ?? '';
  if (result?.status === 'complete') {
    try {
      prettyResponse = JSON.stringify(JSON.parse(prettyResponse), null, 2);
    } catch {
      // Not JSON - show as-is.
    }
  }

  return (
    <div className="run-panel">
      <div className="run-panel__inputs">
      <div className="run-panel__header">
        <div className="run-panel__title">Input</div>
        <div className="run-panel__hint">Runs your unsaved edits - no need to save first</div>
      </div>

      <div className="run-panel__section">
        <label className="run-panel__label">Item URL or ID</label>
        <input
          className="run-panel__input"
          value={itemReference}
          onChange={(e) => setItemReference(e.target.value)}
          placeholder="Optional - fills the item-based variables"
          spellCheck={false}
        />
      </div>

      {fields.length > 0 && (
        <div className="run-panel__section">
          <label className="run-panel__label">Variable values</label>
          {fields.map(field => (
            <div key={field.name} className="run-panel__var">
              <code>{`{${field.name}}`}</code>
              <textarea
                className="run-panel__input run-panel__textarea"
                rows={4}
                value={overrides[field.name] ?? ''}
                onChange={(e) => setOverrides(prev => ({ ...prev, [field.name]: e.target.value }))}
                placeholder={itemDriven(field.name) && itemReference.trim() ? 'From the item' : field.description || 'Value for this run'}
              />
            </div>
          ))}
        </div>
      )}

      <button
        className="btn-primary run-panel__run"
        onClick={run}
        disabled={!canRun}
        title={canRun ? 'Run (Ctrl+Enter)' : 'Pick a model and write a user prompt first'}
      >
        <PlayIcon /> {running ? 'Re-run' : result ? 'Run again' : 'Run'}
        <kbd>Ctrl ↵</kbd>
      </button>
      </div>

      <div className="run-panel__output">
      {!result && (
        <div className="run-panel__placeholder">
          Pick an item or fill in the variables, then press <strong>Run</strong>.
        </div>
      )}
      {result && (
        <div className="run-panel__result">
          <div className="run-panel__status">
            <span className={`run-panel__dot run-panel__dot--${result.status}`} />
            {result.status === 'streaming' ? 'Running…' : result.status === 'error' ? 'Failed' : 'Done'}
            {result.durationMs !== undefined && <span className="run-panel__muted">{(result.durationMs / 1000).toFixed(1)}s</span>}
            {result.validation && (
              <span className={result.validation.valid ? 'run-panel__ok' : 'run-panel__bad'}>
                {result.validation.valid ? 'Schema valid' : 'Schema invalid'}
              </span>
            )}
            <button className="run-panel__link" onClick={onEditPrompt}>Edit prompt</button>
          </div>

          {result.error && <div className="run-panel__error">{result.error}</div>}

          {(result.renderedUser || result.renderedSystem) && (
            <details className="run-panel__details" open={showPrompt} onToggle={(e) => setShowPrompt((e.target as HTMLDetailsElement).open)}>
              <summary>Rendered prompt</summary>
              {result.renderedSystem && <pre className="run-panel__pre run-panel__pre--muted">{result.renderedSystem}</pre>}
              <pre className="run-panel__pre">{result.renderedUser}</pre>
            </details>
          )}

          {result.thinking && (
            <details className="run-panel__details">
              <summary>Thinking</summary>
              <pre className="run-panel__pre run-panel__pre--muted">{result.thinking}</pre>
            </details>
          )}

          {result.validation && !result.validation.valid && result.validation.errors !== undefined && (
            <pre className="run-panel__pre run-panel__pre--bad">
              {typeof result.validation.errors === 'string'
                ? result.validation.errors
                : JSON.stringify(result.validation.errors, null, 2)}
            </pre>
          )}

          <pre className="run-panel__pre run-panel__response">
            {prettyResponse || (result.status === 'streaming' ? '…' : '')}
          </pre>
        </div>
      )}
      </div>
    </div>
  );
};
